import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor, VendorActivationStatus } from 'entities/Vendors';

import {
    OrderItemSourcingState,
    OrderItemSourcingValidationError,
} from '../types/EditOrderItemSourcingSchema';

export const validateState = (
    currentState: OrderItemSourcingState,
    availableQuantity: number,
    isPreSubmission: boolean,
    vendorsMap?: Record<string, Vendor> | null,
    proposals?: Proposal[],
): OrderItemSourcingValidationError[] => {
    if (!isPreSubmission) {
        return [];
    }

    const errors: OrderItemSourcingValidationError[] = [];

    // Validate all the totals align with what we would expect
    validateTotals(currentState, availableQuantity, errors);

    // Validate at least one supplier has quantity
    validateSupplierQuantities(currentState, errors);

    // Validate supplier activation status
    validateSupplierActivationStatus(
        currentState,
        vendorsMap,
        proposals,
        errors,
    );

    if (errors.length > 0) {
        console.warn('Sourcing validation errors:', errors);
    }

    return errors;
};

const validateTotals = (
    currentState: OrderItemSourcingState,
    availableQuantity: number,
    errors: OrderItemSourcingValidationError[],
) => {
    // Validate total quantity is positive
    if (currentState.totalQuantity <= 0) {
        errors.push({
            field: 'totalQuantity',
            message: 'Total quantity must be greater than 0',
        });
    }

    // Validate that the total quantity is not greater than the available quantity
    if (currentState.totalQuantity > availableQuantity) {
        errors.push({
            field: 'totalQuantity',
            message:
                'Total quantity must be less than or equal to available quantity',
        });
    }
};

const validateSupplierQuantities = (
    currentState: OrderItemSourcingState,
    errors: OrderItemSourcingValidationError[],
) => {
    const hasSupplierQuantity = currentState.selectedProposals.some(
        (proposal) => (proposal.selectedQuantity ?? 0) > 0,
    );

    if (!hasSupplierQuantity) {
        errors.push({
            field: 'supplierQuantities',
            message:
                'At least one supplier must have a quantity greater than 0',
        });
    }
};

const validateSupplierActivationStatus = (
    currentState: OrderItemSourcingState,
    vendorsMap: Record<string, Vendor>,
    proposals: Proposal[],
    errors: OrderItemSourcingValidationError[],
) => {
    if (vendorsMap) {
        currentState.selectedProposals.forEach((proposal) => {
            if ((proposal.selectedQuantity ?? 0) > 0) {
                // Find the corresponding proposal in the proposals array to get the seller UID
                const sourceProposal = proposals?.find(
                    (p) => p.uid === proposal.uid,
                );
                if (sourceProposal) {
                    const vendor = vendorsMap[sourceProposal.seller.uid];
                    if (vendor) {
                        const status = vendor.operationsAccountStatus;
                        if (
                            status === VendorActivationStatus.VERIFYING ||
                            status === VendorActivationStatus.INVALID
                        ) {
                            errors.push({
                                field: 'supplierQuantities',
                                message: `Supplier "${proposal.name}" has an invalid or pending activation status`,
                                proposalUid: proposal.uid,
                            });
                        }
                    }
                }
            }
        });
    }
};
